import { useState } from "react"
import { Link } from "@inertiajs/react"
import { ChevronLeft, ChevronRight, Clock } from "lucide-react"

export function FeaturedCarousel({ articles }) {
  const [currentIndex, setCurrentIndex] = useState(0)

  if (!articles || articles.length === 0) return null

  const currentArticle = articles[currentIndex]

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? articles.length - 1 : prev - 1))
  }

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === articles.length - 1 ? 0 : prev + 1))
  }

  const formattedDate = currentArticle.publishedAt
    ? new Date(currentArticle.publishedAt).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null

  return (
    <div className="relative bg-white rounded-lg overflow-hidden shadow-lg group">
      <Link
        href={`/collective/${currentArticle.category.slug}/${currentArticle.slug}`}
        className="grid lg:grid-cols-2"
      >
        {/* Image */}
        <div className="aspect-[2/1] lg:aspect-auto lg:h-[400px] relative overflow-hidden bg-zinc-100">
          {currentArticle.featuredImageUrl ? (
            <img
              src={currentArticle.featuredImageUrl}
              alt={currentArticle.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-kf-red/20 to-kf-red/5 flex items-center justify-center">
              <span className="text-kf-red/40 font-display text-4xl">KF</span>
            </div>
          )}
          {/* Category badge */}
          <span className="absolute top-4 left-4 px-4 py-2 bg-kf-red text-white text-sm font-semibold uppercase tracking-wider">
            {currentArticle.category.name}
          </span>
        </div>

        {/* Content */}
        <div className="p-8 lg:p-12 flex flex-col justify-center">
          <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
            {formattedDate && <span>{formattedDate}</span>}
            {currentArticle.readTime && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{currentArticle.readTime} min read</span>
              </div>
            )}
          </div>

          <h2 className="font-display text-2xl lg:text-3xl text-zinc-900 mb-4">
            {currentArticle.title}
          </h2>

          <p className="text-zinc-600 mb-6 line-clamp-3">
            {currentArticle.excerpt}
          </p>

          <div className="flex items-center gap-4 mb-8">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-kf-gradient flex items-center justify-center flex-shrink-0">
              {currentArticle.author.headshotUrl ? (
                <img
                  src={currentArticle.author.headshotUrl}
                  alt={currentArticle.author.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-white text-sm font-display">
                  {currentArticle.author.name?.charAt(0) || "K"}
                </span>
              )}
            </div>
            <div>
              <p className="font-semibold text-zinc-900">{currentArticle.author.name}</p>
            </div>
          </div>

          <span className="inline-flex items-center gap-2 text-kf-red font-semibold group-hover:underline">
            Read Article
            <ChevronRight className="w-4 h-4" />
          </span>
        </div>
      </Link>

      {/* Navigation arrows */}
      {articles.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg text-zinc-700 hover:text-kf-red transition-colors"
            aria-label="Previous article"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg text-zinc-700 hover:text-kf-red transition-colors"
            aria-label="Next article"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}

      {/* Dots */}
      {articles.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {articles.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex ? "bg-kf-red" : "bg-zinc-300 hover:bg-zinc-400"
              }`}
              aria-label={`Go to article ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
